import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SaveContinueLaterDialog } from './questions/questions.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SharedDataService } from 'src/services/shared-data.service';
import { GoogleAnalyticsService } from './core/google-analytics.service';
import { environment } from 'src/environments/environment';
import { I18nService } from '@app/core/i18n.service';
import { first } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],

})
export class AppComponent implements OnInit {
  idleState = 'Not started.';
  timedOut = false;
  lastPing?: any = null;
  title = 'angular-idle-timeout';
  TIMEOUT_COUNT1: number = 1800; // 1800 for 30 mins
  TIMEOUT_COUNT2: number = 10; // 1800 for 30 mins
  dialogText: any = 'You Have Been Idle!';
  dialogRef: any = null;
  ignoreTimeout: any = ['/error', '/surveyClosed', '/thankyou'];
  isPhonePortrait = false;
  isPreviewMode: boolean = false;
  underMaintenance: boolean = false;
  localizedUnderMaintenanceMsg: string = $localize`:Under maintenance text |Text message displayed as under maintenance in login page@appUnderMaintenanceMsgText:Our system is currently undergoing maintenance. Please try again later.`;
  constructor(
    private idle: Idle,
    private _gaService: GoogleAnalyticsService,
    private keepalive: Keepalive,
    private _router: Router,
    private router: Router,
    public dialog: MatDialog,
    private responsive: BreakpointObserver,
    private sharedDataService: SharedDataService,
    private cd: ChangeDetectorRef,
    private _i18nService: I18nService,
  ) {

    localStorage.setItem("firstPage", "false");
    //This code is added to prevent headsup msg showing in the top after refreshig the page in undermaintenance page
    router.events.subscribe((event:any) => {
      if (event instanceof NavigationEnd) {

        const errorState = this.router.lastSuccessfulNavigation?.extras.state as { errorCode: number };
        if(this.router.url == '/error' || errorState?.errorCode==503){
          this.underMaintenance = true;    
        }

        if(this.router.url.indexOf('/launch/')>-1){
          this.underMaintenance = false; 
        }else{
          this.underMaintenance = true; 
        }

        //This timeout is added so that after loading intro page and local storage value is set
        setTimeout(()=>{
          const firstPage = localStorage.getItem('firstPage')?.toString();
          if(firstPage=="true"){
            this.underMaintenance = false;
          }
        },1000)
      }
    });

    // Redirect to error page if system is under maintainence.
    // Under maintenance need to show a small bubble message in the screen.
    if (this.isSurveyInstrumentsUnderMaintenance) {
      this._router.navigate(['error'], {
        state: {
          errorCode: 503
        }
      });
      this.underMaintenance = true;
    }else{
      this.underMaintenance = false;
    }

    idle.setIdle(this.TIMEOUT_COUNT1);
    idle.setTimeout(this.TIMEOUT_COUNT2);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => {
      this.idleState = 'No longer idle.'
      this.reset();
    });

    idle.onTimeout.subscribe(() => {
      this.idleState = 'Timed out!';
      this.timedOut = true;
    });

    idle.onIdleStart.subscribe(() => {
      this.idleState = 'You\'ve gone idle!';
      let url = window.location.href;
      let ignoreFlag = false;
      this.ignoreTimeout.array.forEach((element: string) => {
        if (url.indexOf(element) > -1) {
          ignoreFlag = true;
        }
      });

      if (ignoreFlag) {
        this.reset();
      } else {
        this.saveContinueLater();
      }
    });

    idle.onTimeoutWarning.subscribe((countdown) => {
      this.idleState = 'You will time out in ' + countdown + ' seconds!'
    });

    // sets the ping interval to 15 seconds
    keepalive.interval(15);

    keepalive.onPing.subscribe(() => this.lastPing = new Date());

    this.reset();
  }


  get isSurveyInstrumentsUnderMaintenance(): boolean {
    if (environment.underMaintenance.activated !== undefined) {
      return environment.underMaintenance.activated;
    } else if (environment.underMaintenance.activationDate !== undefined) {
      const activatingIn = new Date(environment.underMaintenance.activationDate).getTime() - new Date().getTime();
      return activatingIn < 0;
    } else {
      return false;
    }
  }

  get isSurveyInstrumentsHeadsupEnabled(): boolean {
    return !this.isSurveyInstrumentsUnderMaintenance && environment.underMaintenance.headsup.enabled !== undefined && environment.underMaintenance.headsup.enabled;
  }

  get getLocalizedSurveyInstrumentsHeadsupStatement(): string {
    return environment.underMaintenance.headsup.statement[this._i18nService.selectedLocaleId as 'en-ca' | 'fr-ca'];
  }

  ngOnInit(): void {

    // initialize google analytics
    this._gaService.init();

    this.responsive.observe([Breakpoints.Small, Breakpoints.HandsetPortrait])
      .subscribe(result => {
        this.isPhonePortrait = false;
        if (result.matches) {
          this.isPhonePortrait = true;
        }
      });

    this.sharedDataService.previewMode$.subscribe(isPreviewMode => {
      this.isPreviewMode = isPreviewMode;
      // added code below as I was getting the following error 
      //NG0100: Expression has changed after it was checked (https://angular.io/errors/NG0100)     
      this.cd.detectChanges();
    });

  }

  reset() {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  saveContinueLater() {
    this.dialog.open(SaveContinueLaterDialog, {
      data: {
        text: window.location.href
      },
      width: '735px',
      height: '380px',
    });
  }

  redirectTo(uri: string) {
    if (uri.indexOf("?") > -1) {
      uri += '&refreshTime=' + (new Date()).getTime();
    } else {
      window.location.reload();
    }
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([uri])
    });
  }
}
